import './header.scss';
import logo from '../../assets/images/onsolt_logo.png';
import profile from '../../assets/images/profile.png';
import { useContext, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Avatar } from 'primereact/avatar';
import { useWindowSize } from 'react-use';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { routes } from '../../routes/allRoutes';
import { AuthCtx } from '../../context/AuthCtxProvider';
import { _UserType } from '../../constants/staticTypes';
import { BROADCAST_MESSAGES } from '../../constants/constants';
import {
    ADMIN_STAFF,
    LOGOUT,
    MY_PROFILE,
    ORG_ADMIN,
    ORG_STAFF,
    SETTINGS,
    SUPER_ADMIN,
    ORG_NAME,
    ORGANIZATION,
    FREE_PLAN,
} from '../../constants/strings';
import { useTranslation } from '../../hooks/useTranslation';
import { getOrgSubscriptionDetails } from '../../helpers/orgSubscriptionHelper';
import { _TmaBranding, getTmaBranding } from '../../helpers/tmaBrandingHelper';
import { ORG_SUBSCRIPTION_DETAILS, TMA_BRANDING } from '../../constants/queryKeys';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';
import { useNavigate } from 'react-router-dom';

const languageTexts = {
    SETTINGS: SETTINGS,
    LOGOUT: LOGOUT,
    SUPER_ADMIN: SUPER_ADMIN,
    ADMIN_STAFF: ADMIN_STAFF,
    ORG_ADMIN: ORG_ADMIN,
    ORG_STAFF: ORG_STAFF,
    MY_PROFILE: MY_PROFILE,
    ORG_NAME: ORG_NAME,
    ORGANIZATION: ORGANIZATION,
    FREE_PLAN: FREE_PLAN,
    UPGRADE: 'Upgrade',
};

function HeaderComponent() {
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const { userType, userData, isAdminToOrgAdmin, setIsAdminToOrgAdmin } = useContext(AuthCtx);

    // Fetch organization subscription details (plan info) — only for org users
    const isOrgUser = userType === 'organizationAdmin' || userType === 'organizationStaff';
    const { data: subscriptionResponse } = useQuery({
        queryKey: [ORG_SUBSCRIPTION_DETAILS],
        queryFn: () => getOrgSubscriptionDetails(queryClient),
        refetchOnWindowFocus: false,
        retry: false,
        enabled: isOrgUser,
    });
    // Extract plan info from API
    const apiData = subscriptionResponse?.data?.data;
    const planName = apiData?.organizationProductPlan?.plan?.name || translatedText.FREE_PLAN;

    // Super admin portal branding (set on the admin Settings page) — only for admin users.
    // Shares the Settings page query, so a save or delete there updates the header straight away.
    const isAdminUser = userType === 'superAdmin' || userType === 'adminStaff';
    const { data: brandingResponse } = useQuery({
        queryKey: TMA_BRANDING,
        queryFn: () => getTmaBranding(queryClient),
        refetchOnWindowFocus: false,
        retry: false,
        enabled: isAdminUser,
    });
    const branding: _TmaBranding | undefined = isAdminUser ? brandingResponse?.data?.data : undefined;
    const webLogo = branding?.webLogo || logo;
    const mobileLogo = branding?.mobileLogo || null;

    const panelRef = useRef<any>(null);
    const { width: screenWidth } = useWindowSize();
    const navigate = useNavigate();

    const [isSidebarToggled, setIsSidebarToggled] = useState(false);

    // Custom dropdown content, not using MenuItem[]

    const handleToggle = () => {
        setIsSidebarToggled(!isSidebarToggled);
    };

    const getUserType = (userType: _UserType | null) => {
        switch (userType) {
            case 'adminStaff':
                return translatedText.ADMIN_STAFF;
            case 'superAdmin':
                return translatedText.SUPER_ADMIN;
            case 'organizationAdmin':
                return translatedText.ORG_ADMIN;
            case 'organizationStaff':
                return translatedText.ORG_STAFF;
            default:
                return '';
        }
    };

    useEffect(() => {
        if (isSidebarToggled) {
            document.body.classList.add('toggle-sidebar');
        } else {
            document.body.classList.remove('toggle-sidebar');
        }
    }, [isSidebarToggled]);

    return (
        <header id="header" className="header fixed-top d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center justify-content-between">
                <i className="ri-menu-line toggle-sidebar-btn" role="button" onClick={(e) => handleToggle()}></i>
                <a href={routes.dashboard} className="logo d-flex align-items-center">
                    {screenWidth >= 625 ? <img src={webLogo} alt="" /> : mobileLogo && <img className="header-mobile-logo" src={mobileLogo} alt="" />}
                </a>
            </div>

            {isAdminToOrgAdmin && window.location.pathname.startsWith('/organization') && (
                <div className="d-flex align-items-center justify-content-between">
                    <small>
                        {screenWidth < 850 ? (
                            ''
                        ) : (
                            <>
                                Currently viewing <strong>{localStorage.getItem('Organization-Name')}</strong> pages,
                            </>
                        )}
                    </small>
                    <a
                        href={routes.dashboard}
                        className="logo d-flex align-items-center ms-2  text-decoration-none"
                        onClick={(e) => {
                            setIsAdminToOrgAdmin(false);
                            localStorage.removeItem('isAdminToOrgAdmin');
                        }}
                    >
                        <i className="pi pi-arrow-left me-2"></i>
                        {screenWidth < 400 ? <></> : <small>Back to Admin pages</small>}
                    </a>
                </div>
            )}

            <div className="header-nav pe-2 d-flex flex-row align-items-center">
                <LanguageSwitcher />
                <Avatar
                    className="header-profile-img"
                    image={userData?.signedUrl ? userData?.signedUrl : profile}
                    imageFallback={profile}
                    shape="circle"
                />
                <div
                    className="header-dropdown d-flex flex-row align-items-center"
                    role="button"
                    onClick={(event: any) => panelRef?.current?.toggle(event)}
                >
                    <div className="profile-info ms-2 me-3 d-flex flex-column">
                        <span>
                            {userData?.firstName} {userData?.lastName}
                        </span>
                        <small className="text-muted">{getUserType(userType)}</small>
                    </div>
                    <i className="pi pi-angle-down" />
                    <OverlayPanel ref={panelRef} className="header-dropdown-panel custom-profile-dropdown">
                        {userType === 'organizationAdmin' || userType === 'organizationStaff' ? (
                            <div className="dropdown-content">
                                <div className="dropdown-org-info">
                                    <div className="dropdown-org-label">{translatedText.ORG_NAME || 'Org Name'}</div>
                                    <div className="dropdown-org-value">{userData?.organizationName || translatedText.ORGANIZATION}</div>
                                </div>
                                <div className="dropdown-divider mb-3" />
                                <div className="dropdown-plan-row">
                                    <span className="dropdown-plan-label">{planName}</span>
                                    <button
                                        className="dropdown-upgrade-btn"
                                        onClick={() => {
                                            panelRef?.current?.hide();
                                            navigate(`${routes.orgSubscriptions}?tab=all`);
                                        }}
                                    >
                                        {translatedText.UPGRADE || 'Upgrade'}
                                    </button>
                                </div>
                                <div className="dropdown-divider" />
                                <div
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        panelRef?.current?.hide();
                                        navigate(routes.orgProfile);
                                    }}
                                >
                                    <i className="pi pi-user me-2" /> {translatedText.MY_PROFILE || 'My Profile'}
                                </div>
                                <div
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        panelRef?.current?.hide();
                                        setIsAdminToOrgAdmin(false);
                                        window.postMessage(BROADCAST_MESSAGES.FORCE_LOGOUT, window.location.origin);
                                    }}
                                >
                                    <i className="pi pi-sign-out me-2" /> {translatedText.LOGOUT || 'Logout'}
                                </div>
                            </div>
                        ) : (
                            <div className="dropdown-content">
                                <div
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        panelRef?.current?.hide();
                                        navigate(routes.profile);
                                    }}
                                >
                                    <i className="pi pi-user me-2" /> {translatedText.MY_PROFILE || 'My Profile'}
                                </div>
                                <div
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        panelRef?.current?.hide();
                                        setIsAdminToOrgAdmin(false);
                                        window.postMessage(BROADCAST_MESSAGES.FORCE_LOGOUT, window.location.origin);
                                    }}
                                >
                                    <i className="pi pi-sign-out me-2" /> {translatedText.LOGOUT || 'Logout'}
                                </div>
                            </div>
                        )}
                    </OverlayPanel>
                </div>
            </div>
        </header>
    );
}

export default HeaderComponent;
